/**
 * TeamingPartnerLibrary — read-only view of linked teaming partners' KB.
 *
 * For each teaming partner in the pursuit that is linked to a client org
 * (partner_org_id), this surfaces that org's Capabilities, Past Performance,
 * and Certifications — live (read-only) — under Manage Documents, so the
 * proposal team can reference the partner's qualifications without leaving
 * the pursuit. Nothing is copied; it reads the partner org's KB directly.
 */
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Users, Award, Briefcase, ChevronDown, ChevronRight, Building2 } from 'lucide-react';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtMoney = (v) =>
  v ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v) : null;

export default function TeamingPartnerLibrary({ pursuitId, authToken }) {
  const [partners, setPartners] = useState(null);  // [{org_id, name, capabilities, pps, certs}]
  const [open, setOpen] = useState({});

  const auth = { Authorization: `Bearer ${authToken}` };

  const load = useCallback(async () => {
    if (!pursuitId || !authToken) return;
    try {
      // Pull the pursuit to find linked teaming partners
      const pRes = await fetch(`${API_URL}/api/pursuits/${pursuitId}`, { headers: auth });
      if (!pRes.ok) { setPartners([]); return; }
      const pursuit = await pRes.json();
      const linked = (pursuit.teaming_plan || []).filter(tp => tp.partner_org_id);
      if (linked.length === 0) { setPartners([]); return; }

      const results = await Promise.all(linked.map(async (tp) => {
        const orgId = tp.partner_org_id;
        const [orgRes, ppRes, certRes] = await Promise.all([
          fetch(`${API_URL}/api/clients/${orgId}`, { headers: auth }),
          fetch(`${API_URL}/api/knowledge-base/past-performances?organization_id=${orgId}`, { headers: auth }),
          fetch(`${API_URL}/api/knowledge-base/certifications?organization_id=${orgId}`, { headers: auth }),
        ]);
        const org = orgRes.ok ? await orgRes.json() : {};
        const pps = ppRes.ok ? (await ppRes.json()).items || [] : [];
        const certs = certRes.ok ? (await certRes.json()).items || [] : [];
        return {
          org_id: orgId,
          name: org.name || tp.legal_name || tp.partner_abbr || 'Partner',
          role: tp.role || '',
          workshare: tp.workshare || 0,
          capabilities: org.capabilities_summary || '',
          pps, certs,
        };
      }));
      setPartners(results);
      // default-expand the first partner
      if (results[0]) setOpen({ [results[0].org_id]: true });
    } catch {
      setPartners([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pursuitId, authToken]);

  useEffect(() => { load(); }, [load]);

  // Hide entirely when there are no linked partners
  if (partners === null || partners.length === 0) return null;

  return (
    <Card className="border-indigo-200" data-testid="teaming-partner-library">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <Users className="w-5 h-5 text-indigo-600" /> Teaming Partner Library
          <Badge variant="secondary" className="ml-1 text-xs">{partners.length}</Badge>
        </CardTitle>
        <p className="text-xs text-slate-500">
          Read-only capabilities, past performance &amp; certifications from your linked teaming partners.
        </p>
      </CardHeader>
      <CardContent className="space-y-2">
        {partners.map((p) => {
          const isOpen = !!open[p.org_id];
          return (
            <div key={p.org_id} className="border border-slate-200 rounded-lg overflow-hidden">
              <button type="button"
                onClick={() => setOpen(o => ({ ...o, [p.org_id]: !o[p.org_id] }))}
                className="w-full flex items-center gap-2 px-3 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors text-left">
                {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                <Building2 className="w-4 h-4 text-indigo-500" />
                <span className="font-medium text-sm text-slate-900">{p.name}</span>
                {p.role && <span className="text-xs text-slate-400">· {p.role}</span>}
                {p.workshare > 0 && <Badge variant="outline" className="ml-auto text-[10px]">{p.workshare}% workshare</Badge>}
              </button>

              {isOpen && (
                <div className="p-3 space-y-3 bg-white">
                  {/* Capabilities */}
                  {p.capabilities ? (
                    <div>
                      <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Capabilities</div>
                      <p className="text-xs text-slate-700 leading-relaxed">{p.capabilities}</p>
                    </div>
                  ) : null}

                  {/* Certifications */}
                  <div>
                    <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                      <Award className="w-3 h-3" /> Certifications ({p.certs.length})
                    </div>
                    {p.certs.length === 0 ? (
                      <p className="text-xs text-slate-400 italic">None recorded</p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {p.certs.map((c) => (
                          <Badge key={c.id} variant="outline" className={cn('text-[11px]',
                            c.status === 'ACTIVE' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200')}>
                            {c.name}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Past performance */}
                  <div>
                    <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                      <Briefcase className="w-3 h-3" /> Past Performance ({p.pps.length})
                    </div>
                    {p.pps.length === 0 ? (
                      <p className="text-xs text-slate-400 italic">None recorded</p>
                    ) : (
                      <div className="space-y-1.5">
                        {p.pps.map((pp) => (
                          <div key={pp.id} className="text-xs border border-slate-100 rounded px-2 py-1.5">
                            <p className="font-medium text-slate-800 truncate">{pp.contract_title || 'Untitled contract'}</p>
                            <p className="text-slate-500 truncate">
                              {[pp.client_agency, fmtMoney(pp.contract_value), pp.cpars_rating].filter(Boolean).join(' · ')}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
